import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrgId } from '../auth';
import { useDebounce } from '../shared/useDebounce';

export interface RecipientLinkSuggestion {
  lead: { id: string; name: string; email: string };
  company: { id: string; name: string } | null;
  deals: Array<{ id: string; title: string }>;
}

export function useEmailRecipientLinks(recipientEmails: string[]) {
  const { orgId } = useCurrentOrgId();
  const emailsKey = recipientEmails.filter(Boolean).join(',');
  const debouncedKey = useDebounce(emailsKey, 300);
  const emails = debouncedKey.split(',').filter(Boolean);

  const { data: suggestions = [], isLoading } = useQuery({
    queryKey: ['email-recipient-links', orgId, debouncedKey],
    queryFn: async (): Promise<RecipientLinkSuggestion[]> => {
      if (!orgId || emails.length === 0) return [];

      // Find leads matching recipient emails
      const { data: leads, error } = await supabase
        .from('leads')
        .select('id, name, email, company_id, companies(id, name)')
        .eq('org_id', orgId)
        .in('email', emails)
        .limit(10);

      if (error || !leads || leads.length === 0) return [];

      const results: RecipientLinkSuggestion[] = [];

      for (const lead of leads) {
        const company = (lead as any).companies
          ? { id: (lead as any).companies.id, name: (lead as any).companies.name }
          : null;

        // Find deals linked to this lead
        const { data: deals } = await supabase
          .from('deals')
          .select('id, title')
          .eq('org_id', orgId)
          .eq('lead_id', lead.id)
          .limit(5);

        results.push({
          lead: { id: lead.id, name: lead.name || '', email: lead.email || '' },
          company,
          deals: deals || [],
        });
      }

      return results;
    },
    enabled: !!orgId && emails.length > 0,
    staleTime: 30_000,
  });

  return { suggestions, isLoading };
}
